import React from 'react'
import _ from 'lodash';
import { AgTechButtonProps, AgTechButton, AgTechClickEventHandler } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechFormTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextControl';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import { AgTechFormSection } from '@agtech/web/components/Forms/Layout/AgTechFormSection';
import { BidderContactRegistration, useBidderDataRegisterNewContactAction } from '@sales/data/bidders/BidderDataActions';
import { useBidderDataGetSaleBidderDataQuery } from '@sales/data/bidders/BidderDataQueries';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { getNextBidderNumber } from '@sales/data/bidders/BidderData';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { DefaultAgTechContact } from '@agtech/data/contacts/ContactData';

export type BidderRegisterNewContactDialogProps = {
    bidderNumber?: string,
    onContactRegistered?: (registeredContact: BidderContactRegistration) => Promise<void>
}

const BidderRegisterNewContactDialog = (props: BidderRegisterNewContactDialogProps) => {
    const registerNewContactAction = useBidderDataRegisterNewContactAction();
    const getSaleBiddersQuery = useBidderDataGetSaleBidderDataQuery();

    return (
        <AgTechFormDialog
            width={700}
            dialogData={{
                query: getSaleBiddersQuery,
                getDialogData: (bidderData) => {
                    const bidders = Object.values(bidderData.bidders);

                    const bidderContact: BidderContactRegistration = {
                        ...DefaultAgTechContact,
                        bidderId: 0,
                        bidderNumber: props.bidderNumber ?? getNextBidderNumber(bidders).toString()
                    }

                    return bidderContact;
                }
            }}
            dialogAction={{
                action: registerNewContactAction,
                getActionData: d => d
            }}
            structure={() => ({
                header: 'Register New Contact',
                submitButtonText: 'Register'
            })}
            dialogBehavior={() => ({
                eventHandlers: {
                    onDialogSubmitted: async contact => {
                        if (props.onContactRegistered) {
                            await props.onContactRegistered({
                                ...contact,
                                bidderNumber: props.bidderNumber ?? ''
                            });
                        }
                    }
                }
            })}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <AgTechFormRow>
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption="First Name"
                            required={true}
                            focused={true}
                            value={entity => entity.firstName}
                            onControlValueChanged={async (e, val) => ({ ...e, firstName: val })}
                        />
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption="Last Name"
                            required={true}
                            value={entity => entity.lastName}
                            onControlValueChanged={async (e, val) => ({ ...e, lastName: val })}
                        />
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption="Bidder Number"
                            required={true}
                            value={entity => entity.bidderNumber}
                            onControlValueChanged={async (e, val) => ({ ...e, bidderNumber: val })}
                        />
                    </AgTechFormRow>
                    <AgTechFormSection header='Contact Information'>
                        <AgTechFormRow>
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="Ranch Name"
                                required={false}
                                value={entity => entity.ranchName}
                                onControlValueChanged={async (e, val) => ({ ...e, ranchName: val })}
                            />
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="Email Address"
                                required={false}
                                value={entity => entity.emailAddress}
                                onControlValueChanged={async (e, val) => ({ ...e, emailAddress: val })}
                            />
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="Phone Number"
                                value={entity => entity.phoneNumber}
                                onControlValueChanged={async (e, val) => ({ ...e, phoneNumber: val })}
                            />
                        </AgTechFormRow>
                    </AgTechFormSection>
                    <AgTechFormSection header="Address Information">
                        <AgTechFormRow>
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="Address"
                                required={true}
                                value={entity => entity.address}
                                width={66}
                                onControlValueChanged={async (e, val) => ({
                                    ...e,
                                    address: val
                                })}
                            />
                        </AgTechFormRow>
                        <AgTechFormRow>
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="City"
                                required={true}
                                value={entity => entity.city}
                                onControlValueChanged={async (e, val) => ({
                                    ...e,
                                    city: val
                                })}
                            />
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="State"
                                required={true}
                                value={entity => entity.stateAbbreviation}
                                onControlValueChanged={async (e, val) => ({
                                    ...e,
                                    stateAbbreviation: val
                                })}
                            />
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="Zip"
                                required={true}
                                value={entity => entity.zip}
                                onControlValueChanged={async (e, val) => ({
                                    ...e,
                                    zip: val
                                })}
                            />
                        </AgTechFormRow>
                    </AgTechFormSection>
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
};

BidderRegisterNewContactDialog.Open = (e: AgTechClickEventHandler, props: BidderRegisterNewContactDialogProps) => {
    e.dialogs.openDialog({
        dialog: () => <BidderRegisterNewContactDialog {...props} />
    });
}

BidderRegisterNewContactDialog.Button = (props: BidderRegisterNewContactDialogProps & AgTechButtonProps) => {
    let buttonProps: AgTechButtonProps = {
        text: "Register New Contact",
        icon: "fas fa-plus",
        ...props,
        action: e => BidderRegisterNewContactDialog.Open(e, props)
    }

    return (
        <AgTechButton {...buttonProps} />
    );
}

export default BidderRegisterNewContactDialog;